/**
 * db.js — Supabase Data Access Layer
 *
 * Column names in SQL match the app's JS field names exactly (camelCase).
 * Rows are sent to Supabase without any translation — what you read is
 * what gets stored and what comes back.
 */

import { supabase, ORG_ID } from './supabaseClient';

// ─── In-memory cache — diff against this to only write changed rows ──
let _cache = null;

const org = () => ORG_ID;

function changed(a, b) {
  return JSON.stringify(a) !== JSON.stringify(b);
}

// ─── Per-record base versions (ATOMIC SAVE — owner's rule, no exceptions) ───────
// Every record loaded from the server is stamped with `__h`, a hash of exactly what
// the server sent, and that snapshot is remembered. A copy made from the record — a
// form opened an hour ago, a closure holding an old store, a tab left open all day —
// carries the same stamp, so when it is saved the merge RPCs are told precisely what
// the user started from. They then write ONLY what the user changed (down to a single
// field of a single item, via jsonb_merge3 on the server) and never put back a value
// someone else has changed since. Without this, an old copy saved into a fresh store
// was diffed against the fresh data: everyone else's newer values looked like "edits"
// and were reverted. `__h` is never sent to the database.
const ROW_KEYS = ['clients', 'todos', 'monthlyWork', 'employees', 'invoices', 'parentClients',
  'leaveRequests', 'overtimeRequests', 'salaryPayments', 'oneTimeExpenses', 'oneTimeRevenue',
  'subscriptions', 'commissionPayments', 'reports', 'holidays', 'updates', 'tasks', 'mockups',
  'contentCalendars'];
const _rowBase = new Map();
const ROW_BASE_MAX = 20000;

function hashStr(str) {                                // cyrb53 — fast, 53-bit
  let h1 = 0xdeadbeef, h2 = 0x41c6ce57;
  for (let i = 0; i < str.length; i++) {
    const ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2654435761); h2 = Math.imul(h2 ^ ch, 1597334677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);
  return (4294967296 * (2097151 & h2) + (h1 >>> 0)).toString(36);
}
export function stripH(o) {
  if (!o || typeof o !== 'object' || Array.isArray(o) || !('__h' in o)) return o;
  const { __h, ...rest } = o; // eslint-disable-line no-unused-vars
  return rest;
}
// Remember this exact version of a record; returns its stamp.
function rememberRow(ns, id, row) {
  const json = JSON.stringify(stripH(row));
  const h = hashStr(json);
  const key = ns + '|' + id + '|' + h;
  if (!_rowBase.has(key)) {
    _rowBase.set(key, JSON.parse(json));
    if (_rowBase.size > ROW_BASE_MAX) _rowBase.delete(_rowBase.keys().next().value);
  }
  return h;
}
function stampStore(store) {
  for (const key of ROW_KEYS) {
    (store[key] || []).forEach(r => { if (r && typeof r === 'object' && r.id != null) r.__h = rememberRow(key, r.id, r); });
  }
  for (const [emp, p] of Object.entries(store.hr || {})) {
    if (p && typeof p === 'object') p.__h = rememberRow('hr', emp, p);
  }
  for (const [emp, c] of Object.entries(store.employeeCredentials || {})) {
    if (c && typeof c === 'object' && !Array.isArray(c)) c.__h = rememberRow('cred', emp, c);
  }
}
// The base a record's edit must be diffed against: the exact version it was copied
// from when known, else the caller's snapshot (fallback).
function baseFor(ns, id, row, fallback) {
  const h = row && row.__h;
  if (h) { const b = _rowBase.get(ns + '|' + id + '|' + h); if (b) return b; }
  return stripH(fallback);
}
// After a successful write the record's base becomes what was written, so the next edit
// of the same copy diffs from there (and never re-sends — and so re-imposes — an older edit).
function restamp(ns, id, row) {
  if (row && typeof row === 'object' && !Array.isArray(row)) row.__h = rememberRow(ns, id, row);
}

// ─── Low-level helpers ────────────────────────────────────────────

async function fetchAll(table) {
  const { data, error } = await supabase
    .from(table)
    .select('*')
    .eq('org_id', org());
  if (error) {
    console.error(`[db] fetchAll(${table}):`, error.message);
    return [];
  }
  // Strip org_id before returning — app never needs it
  return (data || []).map(({ org_id, created_at, updated_at, ...rest }) => rest); // eslint-disable-line
}

async function fetchConfig(key) {
  const { data, error } = await supabase
    .from('app_config')
    .select('data')
    .eq('org_id', org())
    .eq('key', key)
    .maybeSingle();
  if (error) {
    console.error(`[db] fetchConfig(${key}):`, error.message);
    return null;
  }
  return data?.data ?? null;
}

// (No blind upsert helpers exist on purpose: every write goes through a merge RPC.)

async function deleteRows(table, ids) {
  if (!ids || ids.length === 0) return { error: null };
  const { error } = await supabase
    .from(table)
    .delete()
    .eq('org_id', org())
    .in('id', ids);
  return { error };
}

// ─── LOAD ─────────────────────────────────────────────────────────

// Every table the app keeps in its store, and how to shape it. Used for the full load
// and for reloading just the tables that changed (see subscribeToChanges).
const arr = v => (Array.isArray(v) ? v : []);
const TABLE_SPECS = [
  { table: 'clients',             key: 'clients' },
  { table: 'employees',           key: 'employees' },
  { table: 'invoices',            key: 'invoices' },
  { table: 'parent_clients',      key: 'parentClients' },
  // HR is stored one row per employee — reassembled into { emp1: {...}, ... }
  { table: 'hr_data',             key: 'hr', shape: rows => { const hr = {}; rows.forEach(r => { hr[r.employee_id] = r.payload || {}; }); return hr; } },
  { table: 'leave_requests',      key: 'leaveRequests' },
  { table: 'overtime_requests',   key: 'overtimeRequests' },
  { table: 'tasks',               key: 'tasks' },
  { table: 'salary_payments',     key: 'salaryPayments' },
  { table: 'one_time_expenses',   key: 'oneTimeExpenses' },
  { table: 'one_time_revenue',    key: 'oneTimeRevenue' },
  { table: 'subscriptions',       key: 'subscriptions' },
  { table: 'commission_payments', key: 'commissionPayments' },
  { table: 'reports',             key: 'reports' },
  { table: 'holidays',            key: 'holidays', shape: arr },
  { table: 'updates',             key: 'updates' },
  { table: 'content_calendars',   key: 'contentCalendars' },
  { table: 'mockups',             key: 'mockups' },
  { table: 'todos',               key: 'todos', shape: arr },
  { table: 'monthly_work',        key: 'monthlyWork', shape: arr },
];
const CONFIG_SPECS = [
  { cfg: 'credentials',   key: 'employeeCredentials', shape: v => v || {} },
  { cfg: 'theme',         key: 'theme',               shape: v => v || {} },
  { cfg: 'notifications', key: 'notifications',       shape: arr },
  // Runtime feature flags (app_config key 'flags'), e.g. { liveCaptions: false }.
  // Absent/true → feature ON; set false to kill a feature live without a redeploy.
  { cfg: 'flags',         key: 'flags',               shape: v => v || {} },
];
async function fetchSpec(spec) {
  const raw = await fetchAll(spec.table);
  return spec.shape ? spec.shape(raw) : raw;
}
async function fetchConfigs(store) {
  const vals = await Promise.all(CONFIG_SPECS.map(c => fetchConfig(c.cfg)));
  CONFIG_SPECS.forEach((c, i) => { store[c.key] = c.shape(vals[i]); });
}
function commitLoaded(store) {
  stampStore(store);
  _cache = JSON.parse(JSON.stringify(store));
  _dataVersion++;   // fresh data arrived — bump the version so stale writes can't delete
  return store;
}

// ─── Change fingerprints (keeps the free-plan egress down) ────────
// data_stamps() returns one small fingerprint per table (~1 KB in all). A tab records
// the fingerprints of what it loaded; its catch-up checks compare against fresh ones
// and reload only the tables that moved, instead of re-downloading everything. The
// fingerprints are fetched BEFORE the data, so a change landing in between can only
// cause one extra reload later — never a missed one.
let _stamps = null;
async function fetchStamps() {
  try {
    const { data, error } = await supabase.rpc('data_stamps');
    if (error || !data || typeof data !== 'object') return null;
    return data;
  } catch (e) { return null; }
}

export async function loadStore() {
  const stamps = await fetchStamps();
  const store = {};
  const vals = await Promise.all(TABLE_SPECS.map(fetchSpec));
  TABLE_SPECS.forEach((spec, i) => { store[spec.key] = vals[i]; });
  await fetchConfigs(store);
  _stamps = stamps;
  return commitLoaded(store);
}

// Reload only the named tables ('app_config' = the settings / notifications record);
// the rest of the store is kept as last loaded. Falls back to a full load if there is
// no store yet or a name isn't recognised.
export async function loadTables(tables) {
  if (!_cache) return loadStore();
  const names = Array.from(new Set(tables || []));
  const specs = TABLE_SPECS.filter(s => names.includes(s.table));
  const wantConfig = names.includes('app_config');
  if (specs.length + (wantConfig ? 1 : 0) < names.length) return loadStore();
  const stamps = await fetchStamps();
  const store = JSON.parse(JSON.stringify(_cache));
  const vals = await Promise.all(specs.map(fetchSpec));
  specs.forEach((spec, i) => { store[spec.key] = vals[i]; });
  if (wantConfig) await fetchConfigs(store);
  if (stamps && _stamps) names.forEach(t => { _stamps[t] = stamps[t]; });   // only what was reloaded
  else _stamps = null;                                                       // unknown → next check reloads in full
  return commitLoaded(store);
}

// Which tables changed since this tab last loaded them? null = can't tell (reload all).
export async function changedTables() {
  if (!_stamps) return null;
  const now = await fetchStamps();
  if (!now) return null;
  return Object.keys(now).filter(t => now[t] !== _stamps[t]);
}

// Bumped every time fresh data is loaded (initial load + realtime reloads). The
// app records the version a pending write was based on; if it advanced before the
// write flushes, that write is stale and must not delete rows others just added.
let _dataVersion = 0;
export function getDataVersion() { return _dataVersion; }
// A deep snapshot of the last-known SERVER state (the diff base). The app captures
// this at edit time and threads it back into saveStore, so a write is always diffed
// against the exact state the edit was derived from — not the live _cache, which a
// realtime reload may have advanced. Returns null before the first successful load.
export function snapshotBase() { return _cache ? JSON.parse(JSON.stringify(_cache)) : null; }

// ─── SAVE ─────────────────────────────────────────────────────────

export async function saveStore(newStore, opts = {}) {
  if (!newStore) return;
  // Diff against the caller-captured base (the server state the edit was made on).
  // Fall back to _cache only if no base was passed. If neither exists we have never
  // loaded — do NOT push, or we'd overwrite the DB with a blind/stale snapshot.
  const prev = (opts.base != null) ? opts.base : _cache;
  if (prev == null) return;
  // Every write is a job that can be retried: the merge RPCs are 3-way, so sending the
  // same (base, new) twice is harmless. A failed write is retried, and if it still fails
  // the caller is told — it is never silently dropped.
  const jobs = [];
  const job = (label, run, after) => jobs.push({ label, run, after });

  // A record needs writing when it differs from the exact version it was copied from.
  const plan = (ns, newRows, prevRows) => {
    const prevMap = new Map(prevRows.map(r => [r.id, r]));
    const out = [];
    for (const r of newRows) {
      if (!r || r.id == null) continue;
      const known = prevMap.get(r.id) || ((_cache && _cache[ns]) || []).find(x => x && x.id === r.id);
      const isNew = !known && !r.__h;
      if (opts.stale && !isNew) continue;       // stale flush: only ADD brand-new rows
      const base = isNew ? {} : baseFor(ns, r.id, r, known || {});
      const body = stripH(r);
      if (!isNew && !changed(body, base)) continue;
      out.push({ r, base, body });
    }
    return out;
  };

  // ── Array tables — diff row by row ──────────────────────────────
  const arrayTables = [
    { key: 'clients',            table: 'clients' },
    { key: 'todos',              table: 'todos' },
    { key: 'monthlyWork',        table: 'monthly_work' },
    { key: 'employees',          table: 'employees' },
    { key: 'invoices',           table: 'invoices' },
    { key: 'parentClients',      table: 'parent_clients' },
    { key: 'leaveRequests',      table: 'leave_requests' },
    { key: 'overtimeRequests',   table: 'overtime_requests' },
    // NOTE: 'tasks' is handled separately below (3-way field merge).
    { key: 'salaryPayments',     table: 'salary_payments' },
    { key: 'oneTimeExpenses',    table: 'one_time_expenses' },
    { key: 'oneTimeRevenue',     table: 'one_time_revenue' },
    { key: 'subscriptions',      table: 'subscriptions' },
    { key: 'commissionPayments', table: 'commission_payments' },
    { key: 'reports',            table: 'reports' },
    { key: 'holidays',           table: 'holidays' },
    { key: 'updates',            table: 'updates' },
    // NOTE: 'contentCalendars' handled separately below (feedback-preserving deep merge).
    // NOTE: 'mockups' is handled separately below (feedback-preserving merge).
  ];

  for (const { key, table } of arrayTables) {
    const newRows  = newStore[key] || [];
    const prevRows = prev[key]     || [];
    if (!changed(newRows, prevRows)) continue;
    // Each new/changed row goes through row_merge, which writes ONLY what this user
    // changed (new vs the version they started from) into the live row — down to a
    // single item of a list (jsonb_merge3) — so nobody else's edit is ever reverted.
    for (const { r, base, body } of plan(key, newRows, prevRows)) {
      job('row_merge(' + table + ')',
        () => supabase.rpc('row_merge', { p_org: org(), p_table: table, p_id: r.id, p_base: base, p_new: body }),
        () => restamp(key, r.id, r));
    }
    // Explicit deletes ONLY — a deletion is never inferred from a diff. A tab whose
    // snapshot predates a realtime insert would otherwise "miss" the newly-inserted
    // row and delete it: that is the HCW-client (newest row) vanishing bug. A row is
    // removed only when the user actually deleted it (id in opts.deletedIds[key]).
    {
      const toDelete = (opts.deletedIds && opts.deletedIds[key]) || [];
      if (toDelete.length) job('delete(' + table + ')', () => deleteRows(table, toDelete));
    }
  }

  // ── Mockups — preserve client feedback on every editor save ──────
  // mockup_save_keep_feedback never overwrites `data.clientChanges`, per-cell
  // `clientStatus` or `changesTaskId` (owned by the atomic client RPCs), and merges
  // everything else 3-way. Do NOT route mockup writes back through a raw upsert.
  {
    const newRows  = newStore.mockups || [];
    const prevRows = prev.mockups     || [];
    if (changed(newRows, prevRows)) {
      for (const { r, base, body } of plan('mockups', newRows, prevRows)) {
        job('mockup_save_keep_feedback',
          () => supabase.rpc('mockup_save_keep_feedback', {
            p_org: org(), p_id: r.id, p_base: base, p_new: body,
            // Explicit cell deletion: the RPC removes ONLY these ids, never infers one.
            p_deleted: { cells: (opts.deletedIds && opts.deletedIds.mockupCells && opts.deletedIds.mockupCells[r.id]) || [] },
          }),
          () => restamp('mockups', r.id, r));
      }
      const toDelete = (opts.deletedIds && opts.deletedIds.mockups) || [];
      if (toDelete.length) job('delete(mockups)', () => deleteRows('mockups', toDelete));
    }
  }

  // ── Tasks — 3-way field merge so two tabs never clobber each other ──
  // task_merge writes ONLY the fields this user changed onto the live row; status,
  // participation and subtasks are owned by their own atomic RPCs.
  {
    const newRows  = newStore.tasks || [];
    const prevRows = prev.tasks     || [];
    if (changed(newRows, prevRows)) {
      for (const { r, base, body } of plan('tasks', newRows, prevRows)) {
        job('task_merge',
          () => supabase.rpc('task_merge', { p_org: org(), p_id: r.id, p_base: base, p_new: body }),
          () => restamp('tasks', r.id, r));
      }
      // Explicit deletes only: a task is removed only when the user actually deleted it.
      const toDelete = (opts.deletedIds && opts.deletedIds.tasks) || [];
      if (toDelete.length) job('delete(tasks)', () => deleteRows('tasks', toDelete));
    }
  }

  // ── Content calendars — preserve designer feedback on every editor save ──
  // Post content is owned by set_calendar_post_fields; every other post / row /
  // calendar field is merged 3-way by content_calendars_save_keep_feedback.
  {
    const newRows  = newStore.contentCalendars || [];
    const prevRows = prev.contentCalendars     || [];
    if (changed(newRows, prevRows)) {
      for (const { r, base, body } of plan('contentCalendars', newRows, prevRows)) {
        job('content_calendars_save_keep_feedback',
          () => supabase.rpc('content_calendars_save_keep_feedback', {
            p_org: org(), p_id: r.id, p_base: base, p_new: body,
            // Explicit post/row deletion — see the mockup note above.
            p_deleted: {
              posts: (opts.deletedIds && opts.deletedIds.calendarPosts && opts.deletedIds.calendarPosts[r.id]) || [],
              grids: (opts.deletedIds && opts.deletedIds.calendarGrids && opts.deletedIds.calendarGrids[r.id]) || [],
            },
          }),
          () => restamp('contentCalendars', r.id, r));
      }
      const toDelete = (opts.deletedIds && opts.deletedIds.contentCalendars) || [];
      if (toDelete.length) job('delete(content_calendars)', () => deleteRows('content_calendars', toDelete));
    }
  }

  // ── HR data — one row per employee ──────────────────────────────
  // Skipped entirely for a stale write. hr_upsert_keep_timelog writes only the HR
  // fields this tab changed (merged into the live value) and ALWAYS keeps the DB's
  // timelog — punches are written only by punch_clock / set_time_entry.
  if (!opts.stale && changed(newStore.hr, prev.hr)) {
    const prevHR = prev.hr || {};
    for (const [empId, payload] of Object.entries(newStore.hr || {})) {
      if (!payload || typeof payload !== 'object') continue;
      const base = baseFor('hr', empId, payload, prevHR[empId] || {});
      const body = stripH(payload);
      if (!changed(body, base)) continue;
      job('hr_upsert_keep_timelog',
        () => supabase.rpc('hr_upsert_keep_timelog', { p_org: org(), p_emp: empId, p_payload: body, p_base: base }),
        () => restamp('hr', empId, payload));
    }
  }

  // ── Config: credentials, theme, notifications ──────────────────────
  if (!opts.stale) {
    // Credentials: per employee, per field (credentials_merge → jsonb_merge3).
    const newCreds = newStore.employeeCredentials || {}, prevCreds = prev.employeeCredentials || {};
    if (changed(newCreds, prevCreds)) {
      const base = {}, body = {};
      for (const [emp, c] of Object.entries(newCreds)) {
        body[emp] = stripH(c);
        const b = baseFor('cred', emp, c, prevCreds[emp]);
        if (b !== undefined) base[emp] = b;
      }
      for (const emp of Object.keys(prevCreds)) if (!(emp in newCreds)) base[emp] = stripH(prevCreds[emp]);
      if (changed(body, base)) {
        job('credentials_merge',
          () => supabase.rpc('credentials_merge', { p_org: org(), p_base: base, p_new: body }),
          () => { for (const [emp, c] of Object.entries(newCreds)) restamp('cred', emp, c); });
      }
    }
    // Theme: keyed, deep 3-way merge (config_merge).
    if (changed(newStore.theme, prev.theme))
      job('config_merge(theme)',
        () => supabase.rpc('config_merge', { p_org: org(), p_key: 'theme', p_base: prev.theme || {}, p_new: newStore.theme || {} }));
  }
  // Notifications: keyed 3-way merge (by id) so concurrent appends / read-clears compose.
  if (changed(newStore.notifications, prev.notifications))
    job('notifications_merge',
      () => supabase.rpc('notifications_merge', { p_org: org(), p_base: prev.notifications || [], p_new: newStore.notifications || [] }));

  // Run everything; retry what failed (1s, 3s), then report what still failed. A write
  // the server REFUSED (not allowed for this account) is not retried — it can never
  // succeed, and retrying it every few seconds only burns requests; it's reported
  // separately so the app can say so instead of "check your connection".
  const isDenied = err => !!err && (err.code === '42501' || err.code === 'PT403' || err.status === 401 || err.status === 403);
  const denied = [];
  const runOne = async (j) => {
    try {
      const res = await j.run();
      const err = res && res.error;
      if (err) {
        if (isDenied(err)) { denied.push(j); return null; }
        return err.message || String(err);
      }
      if (j.after) j.after();
      return null;
    } catch (e) { return (e && e.message) || String(e); }
  };
  const sleep = ms => new Promise(res => setTimeout(res, ms));
  const attempt = async (batch) => {
    const results = await Promise.all(batch.map(runOne));
    return batch.filter((j, i) => {
      if (results[i]) console.error('[db] ' + j.label + ':', results[i]);
      return !!results[i];
    });
  };
  let pending = await attempt(jobs);
  if (pending.length) { await sleep(1000); pending = await attempt(pending); }
  if (pending.length) { await sleep(3000); pending = await attempt(pending); }
  // A stale or partly refused write didn't fully reconcile against the DB — don't advance _cache to it.
  if (!opts.stale && !pending.length && !denied.length) _cache = JSON.parse(JSON.stringify(newStore));
  _lastWriteTime = Date.now(); // prevent realtime from overwriting our write
  if (pending.length || denied.length) {
    const n = pending.length + denied.length;
    const e = new Error(n + ' change' + (n === 1 ? '' : 's') + " didn't save");
    e.failed = pending.map(j => j.label);
    e.denied = denied.map(j => j.label);
    e.retryable = pending.length > 0;
    throw e;
  }
}

// NOTE: the old saveEmployeeHR() raw whole-payload upsert was REMOVED. It could
// overwrite a fresher punch from a stale snapshot — the exact bug that made
// clock-ins disappear. All HR writes now go through the atomic merge RPCs below
// (punch_clock / set_time_entry) or hr_upsert_keep_timelog in saveStore, none of
// which can ever shrink a timelog. Do not reintroduce a direct hr_data upsert.

// Clock in / out. The ONLY writer of timelog in/out — an atomic server-side
// merge (punch_clock RPC) so a punch can never be overwritten by a stale
// whole-store save from another session. THROWS on failure so the portal can
// tell the employee the punch didn't reach the server. Returns the merged
// payload, which we fold into the diff baseline.
export async function punchClock(empId, date, field, time, device) {
  const { data, error } = await supabase.rpc('punch_clock', {
    p_org: org(), p_emp: empId, p_date: date, p_field: field, p_time: time, p_device: device ?? null,
  });
  if (error) throw new Error(error.message || 'punch failed');
  if (_cache && _cache.hr && data) _cache.hr = { ..._cache.hr, [empId]: data };
  _lastWriteTime = Date.now();
  return data;
}

// Admin timelog correction — targeted single-field merge (set_time_entry RPC).
// Pass '' to clear the field. Never touches other days/fields, so it can't be
// clobbered by (and can't clobber) a whole-store save.
export async function setTimeEntry(empId, date, field, value) {
  const { data, error } = await supabase.rpc('set_time_entry', {
    p_org: org(), p_emp: empId, p_date: date, p_field: field, p_value: value === '' ? null : value,
  });
  if (error) throw new Error(error.message || 'set_time_entry failed');
  if (_cache && _cache.hr && data) _cache.hr = { ..._cache.hr, [empId]: data };
  _lastWriteTime = Date.now();
  return data;
}

// Atomic per-post content write for calendars (caption/postContent/inspiration/etc.).
// These fields are OWNED by this RPC — content_calendars_save_keep_feedback leaves
// them alone for existing posts — so a stale whole-store save can never revert a
// calendar caption/content. Mirrors set_mockup_cell_fields.
export async function setCalendarPostFields(calId, postId, fields) {
  const { error } = await supabase.rpc('set_calendar_post_fields', {
    p_org: org(), p_cal: calId, p_post: postId, p_fields: fields || {},
  });
  if (error) throw new Error(error.message || 'set_calendar_post_fields failed');
}

// Atomic task participation writes. workingOn / completedBy / finalLink / subTasks are
// OWNED by these RPCs — task_merge no longer writes them — so a stale whole-store flush
// can never revert who started, who submitted, the submission link, or a ticked subtask.
export async function taskStart(taskId, empId, name, at) {
  markLocalWrite();
  const { error } = await supabase.rpc('task_start', { p_org: org(), p_id: taskId, p_emp: empId, p_name: name, p_at: at });
  if (error) throw new Error(error.message || 'task_start failed');
}
export async function taskComplete(taskId, empId, name, at, finalLink) {
  markLocalWrite();
  const { error } = await supabase.rpc('task_complete', { p_org: org(), p_id: taskId, p_emp: empId, p_name: name, p_at: at, p_final_link: finalLink || '' });
  if (error) throw new Error(error.message || 'task_complete failed');
}
export async function taskSetSubtask(taskId, subId, done) {
  markLocalWrite();
  const { error } = await supabase.rpc('task_set_subtask', { p_org: org(), p_id: taskId, p_sub: subId, p_done: !!done });
  if (error) throw new Error(error.message || 'task_set_subtask failed');
}

// Atomic write of the whole subtask LIST (add / remove / rename / reorder). The done
// state of each surviving subtask is preserved server-side, since `done` is owned by
// taskSetSubtask. Used when the task editor changes the subtask list — the whole-store
// save skips subTasks, so this is the durable path for structural subtask edits.
export async function taskSetSubtasks(taskId, subTasks) {
  markLocalWrite();
  const { error } = await supabase.rpc('task_set_subtasks', { p_org: org(), p_id: taskId, p_subs: Array.isArray(subTasks) ? subTasks : [] });
  if (error) throw new Error(error.message || 'task_set_subtasks failed');
}

// ─── REAL-TIME ────────────────────────────────────────────────────

/**
 * Subscribe to live changes from other sessions.
 * Returns an unsubscribe function — call it on component unmount.
 */
// Track when we last wrote so realtime doesn't race against our own writes
let _lastWriteTime = 0;
const WRITE_DEBOUNCE_MS = 3000; // ignore realtime events for 3s after a write

// Called from the app when local edits happen (before the debounced save fires)
// so realtime reloads don't clobber in-progress edits that haven't been saved yet.
export function markLocalWrite() { _lastWriteTime = Date.now(); }

// Atomic task status write. A task's status/completedDate are NO LONGER written by
// the whole-store save (task_merge skips them), so a lagging tab can never revert a
// task's lifecycle state. Every status change goes through this instead.
// extra: { reason, changeBy, completedDate } — all optional.
export async function taskSetStatus(taskId, status, extra = {}) {
  markLocalWrite();
  const { data, error } = await supabase.rpc('task_set_status', {
    p_org: org(), p_id: taskId, p_status: status, p_extra: extra,
  });
  if (error) throw new Error(error.message || 'task_set_status failed');
  return data;
}

export function subscribeToChanges(onUpdate) {
  // Every table the app persists. A live change reloads ONLY the table it names; the
  // catch-up triggers below ask the server which tables changed (a ~1 KB check) and
  // reload just those. Re-downloading the whole database on every change and every
  // 3 minutes, in every open tab, was ~1.4 GB a day — over the free plan's egress.
  // (Each table is also a member of the `supabase_realtime` publication.)
  const TABLES = [...TABLE_SPECS.map(s => s.table), 'app_config'];

  let debounceTimer = null;
  let stopped = false;
  let pendingTables = new Set();   // named by live events since the last reload
  let checkPending = false;        // a catch-up asked "what did I miss?"

  // Schedule the reload that brings this tab back in sync. A reload must never run
  // while a local write is still settling — it would advance the save baseline
  // (_cache) out from under the pending flush, making that flush write a stale
  // value. But the event must never be DROPPED either: nothing else re-syncs this
  // tab, so a dropped reload leaves it rendering its own stale snapshot until some
  // OTHER session happens to write. That is the "task I sent to client review came
  // back on the board" bug — every click re-arms the write window (persist,
  // taskSetStatus and each debounced flush all call markLocalWrite), so working
  // down the board back-to-back suppressed every reload while the DB was already
  // correct. So when the window is still hot we RE-ARM and try again rather than
  // return, and the reload lands as soon as the user pauses.
  const scheduleReload = (delay) => {
    if (debounceTimer) clearTimeout(debounceTimer);
    // Debounce multiple rapid changes into one reload.
    debounceTimer = setTimeout(async () => {
      debounceTimer = null;
      if (stopped) return;
      const sinceWrite = Date.now() - _lastWriteTime;
      if (sinceWrite < WRITE_DEBOUNCE_MS) {
        scheduleReload(WRITE_DEBOUNCE_MS - sinceWrite + 100);   // wait out the remainder, then retry
        return;
      }
      const check = checkPending, named = Array.from(pendingTables);
      checkPending = false; pendingTables = new Set();
      try {
        let tables = named;
        if (check) {
          const missed = await changedTables();
          tables = missed === null ? null : Array.from(new Set([...named, ...missed]));
        }
        if (tables !== null && !tables.length) return;           // nothing changed — nothing to fetch
        const updated = tables === null ? await loadStore() : await loadTables(tables);
        if (!stopped) onUpdate(updated);
      } catch (e) {
        console.error('[db] realtime reload failed:', e);
        named.forEach(t => pendingTables.add(t));                // never drop what we were told about
        checkPending = true;
      }
    }, delay);
  };
  const scheduleCheck = (delay) => { checkPending = true; scheduleReload(delay); };

  const handleChange = (payload) => {
    const t = payload && payload.table;
    if (t) pendingTables.add(t); else checkPending = true;
    scheduleReload(1500);
  };

  // Catch-up (the "left the app open" case). Realtime only tells a tab about changes
  // while its socket is connected — a laptop asleep, a dropped Wi-Fi or a background
  // phone misses them, and nothing replays them, so that tab kept editing old data.
  // Re-check whenever it may have missed something: the socket reconnecting, the
  // network coming back, the tab becoming visible after a while, and every few
  // minutes regardless as a safety net. Each check is the cheap fingerprint compare.
  let everJoined = false;
  const onStatus = (status) => {
    if (status === 'SUBSCRIBED') { if (everJoined) scheduleCheck(300); everJoined = true; }
  };
  const channels = TABLES.map(table =>
    supabase
      .channel(`rt_${table}_${Date.now()}`)
      .on('postgres_changes', { event: '*', schema: 'public', table }, handleChange)
      .subscribe(onStatus)
  );
  let hiddenAt = 0;
  const onVisibility = () => {
    if (typeof document === 'undefined') return;
    if (document.hidden) { hiddenAt = Date.now(); return; }
    if (hiddenAt && Date.now() - hiddenAt > 20000) scheduleCheck(200);
    hiddenAt = 0;
  };
  const onOnline = () => scheduleCheck(500);
  const poll = setInterval(() => {
    if (typeof document === 'undefined' || !document.hidden) scheduleCheck(0);
  }, 3 * 60 * 1000);
  if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onVisibility);
  if (typeof window !== 'undefined') window.addEventListener('online', onOnline);

  return () => {
    stopped = true;
    if (debounceTimer) clearTimeout(debounceTimer);
    clearInterval(poll);
    if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVisibility);
    if (typeof window !== 'undefined') window.removeEventListener('online', onOnline);
    channels.forEach(ch => supabase.removeChannel(ch));
  };
}

// ─── ACTIVITY FEED ────────────────────────────────────────────────
// task_events is append-only: one row per task status transition, written by the
// log_task_event trigger since August. The dashboard ticker reads it directly rather
// than going through the store, so an event shows up without a whole-store reload.

export async function fetchRecentActivity(limit = 30) {
  const { data, error } = await supabase
    .from('task_events')
    .select('id, task_id, task_name, client_name, old_status, new_status, changed_at')
    .eq('org_id', org())
    .order('changed_at', { ascending: false })
    .limit(limit);
  if (error) { console.error('[db] fetchRecentActivity:', error.message); return []; }
  return data || [];
}

// Live push for new events. Each INSERT goes straight to the callback — no refetch —
// so this stays cheap even when the whole team is working the board. Deliberately NOT
// routed through subscribeToChanges, which reloads the entire store on any change.
// Returns an unsubscribe.
export function subscribeToActivity(onEvent) {
  const ch = supabase
    .channel('rt_activity_' + Date.now())
    .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'task_events' },
      payload => {
        const row = payload && payload.new;
        if (row && row.org_id === org()) onEvent(row);
      })
    .subscribe();
  return () => { try { supabase.removeChannel(ch); } catch (e) { /* already gone */ } };
}
